/**
 * E15 限时任务管理页面
 *
 * 功能：限时任务的增删改查、启用/禁用切换
 * 数据来源：shopping-mall 服务 limited_task 表
 */
import React, { useState } from 'react';
import { Card, Table, Button, Tag, Space, Modal, message, Form, Select } from 'antd';
import { PlusOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTable } from '@/hooks/useTable';
import { getLimitedTaskList, deleteLimitedTask, toggleLimitedTaskStatus } from './api';
import type { LimitedTask, LimitedTaskListParams } from './types';
import LimitedTaskDrawer from './LimitedTaskDrawer';
import dayjs from 'dayjs';

/** 状态标签映射 */
const STATUS_MAP: Record<number, { text: string; color: string }> = {
  0: { text: '禁用', color: 'default' },
  1: { text: '启用', color: 'processing' },
};

/** 奖励类型映射 */
const REWARD_TYPE_MAP: Record<string, string> = {
  fixed: '固定金币',
  percent: '百分比加成',
};

const LimitedTaskPage: React.FC = () => {
  const [form] = Form.useForm();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<LimitedTask>();

  const {
    data,
    isLoading,
    handleTableChange,
    currentPage,
    pageSize,
    total,
    refresh,
  } = useTable({
    queryKey: 'limited-task-list',
    defaultPageSize: 10,
    queryFn: async (current, size, params) => {
      const response = await getLimitedTaskList({
        ...params,
        currentPage: current,
        pageSize: size,
      });
      return {
        records: response.data?.records || response.data || [],
        total: response.data?.total || 0,
      };
    },
  });

  /** 打开创建抽屉 */
  const handleCreate = () => {
    setEditingTask(undefined);
    setIsDrawerOpen(true);
  };

  /** 打开编辑抽屉 */
  const handleEdit = (record: LimitedTask) => {
    setEditingTask(record);
    setIsDrawerOpen(true);
  };

  /** 切换启用/禁用 */
  const handleToggleStatus = async (record: LimitedTask) => {
    const newStatus = record.status === 1 ? 0 : 1;
    await toggleLimitedTaskStatus(record.id!, newStatus);
    message.success(newStatus === 1 ? '已启用' : '已禁用');
    refresh();
  };

  /** 删除 */
  const handleDelete = (record: LimitedTask) => {
    Modal.confirm({
      title: '确认删除',
      icon: <ExclamationCircleOutlined />,
      content: `确认删除限时任务「${record.nameEn}」？`,
      onOk: async () => {
        await deleteLimitedTask(record.id!);
        message.success('删除成功');
        refresh();
      },
    });
  };

  /** 判断任务时间状态 */
  const getTimeStatus = (record: LimitedTask) => {
    const now = dayjs();
    const start = dayjs(record.startTime);
    const end = dayjs(record.endTime);
    if (now.isBefore(start)) return { text: '未开始', color: 'default' };
    if (now.isAfter(end)) return { text: '已结束', color: 'error' };
    return { text: '进行中', color: 'success' };
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '编码', dataIndex: 'code', width: 150 },
    { title: '英文名', dataIndex: 'nameEn', width: 180 },
    { title: '日文名', dataIndex: 'nameJa', width: 180 },
    {
      title: '奖励',
      width: 120,
      render: (_: any, record: LimitedTask) => (
        <span>{REWARD_TYPE_MAP[record.rewardType] || record.rewardType}: {record.rewardValue}</span>
      ),
    },
    {
      title: '风格/类型',
      width: 120,
      render: (_: any, record: LimitedTask) => (
        <span>
          {record.styleId ? `风格:${record.styleId}` : '不限'}
          {record.drawType != null ? ` / ${record.drawType === 0 ? '文生图' : '图生图'}` : ''}
        </span>
      ),
    },
    {
      title: '活动时间',
      width: 200,
      render: (_: any, record: LimitedTask) => (
        <div>
          <div>{dayjs(record.startTime).format('MM/DD HH:mm')}</div>
          <div>~ {dayjs(record.endTime).format('MM/DD HH:mm')}</div>
        </div>
      ),
    },
    {
      title: '时间状态',
      width: 80,
      render: (_: any, record: LimitedTask) => {
        const s = getTimeStatus(record);
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (status: number) => {
        const s = STATUS_MAP[status] || STATUS_MAP[0];
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: '操作',
      width: 200,
      render: (_: any, record: LimitedTask) => (
        <Space>
          <Button type="link" size="small" onClick={() => handleEdit(record)}>编辑</Button>
          <Button type="link" size="small" onClick={() => handleToggleStatus(record)}>
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
          <Button type="link" size="small" danger onClick={() => handleDelete(record)}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="限时任务管理"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          创建限时任务
        </Button>
      }
    >
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
      />

      <LimitedTaskDrawer
        open={isDrawerOpen}
        editingTask={editingTask}
        onClose={() => setIsDrawerOpen(false)}
        onSuccess={refresh}
      />
    </Card>
  );
};

export default LimitedTaskPage;
